import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { WebhooksPage } from "../../../components/dashboard/webhooks";
import { useOneTimePageAnimation } from "@/components/motion";
import {
  createWebhookAction,
  deleteWebhookAction,
  getWebhooksView,
  resendWebhookVerificationAction,
  rotateWebhookSecretAction,
  testWebhookAction,
  updateWebhookAction,
  verifyWebhookAction,
} from "@/features/webhooks/server-fns";
import { webhooksQueryOptions } from "@/features/webhooks/queries";
import { capturePostHogEvent } from "@/lib/posthog-client";

export const Route = createFileRoute("/dashboard/webhooks/")({
  head: () => ({
    meta: [{ title: "Webhooks - Bavimail" }],
  }),
  staleTime: 30_000,
  preloadStaleTime: 30_000,
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData(webhooksQueryOptions());
  },
  component: WebhooksRoute,
});

function WebhooksRoute() {
  const queryClient = useQueryClient();
  const { data: webhooks } = useSuspenseQuery(webhooksQueryOptions());
  const webhookAnimationStage = useOneTimePageAnimation("dashboard-webhooks-list");

  const createMutation = useMutation({
    mutationFn: createWebhookAction,
    onSuccess: (created) => {
      capturePostHogEvent("webhook_created");
      queryClient.setQueryData<Awaited<ReturnType<typeof getWebhooksView>>>(
        ["webhooks"],
        (current) => {
          if (!current) return [created.webhook];
          return [created.webhook, ...current.filter((item) => item.id !== created.webhook.id)];
        },
      );
    },
  });

  const updateMutation = useMutation({
    mutationFn: updateWebhookAction,
    onSuccess: (updated) => {
      queryClient.setQueryData<Awaited<ReturnType<typeof getWebhooksView>>>(
        ["webhooks"],
        (current) => current?.map((item) => (item.id === updated.id ? updated : item)) ?? current,
      );
    },
  });

  const verifyMutation = useMutation({
    mutationFn: verifyWebhookAction,
    onSuccess: (verified) => {
      queryClient.setQueryData<Awaited<ReturnType<typeof getWebhooksView>>>(
        ["webhooks"],
        (current) => current?.map((item) => (item.id === verified.id ? verified : item)) ?? current,
      );
    },
  });

  const resendMutation = useMutation({
    mutationFn: resendWebhookVerificationAction,
    onSuccess: (resent) => {
      queryClient.setQueryData<Awaited<ReturnType<typeof getWebhooksView>>>(
        ["webhooks"],
        (current) => current?.map((item) => (item.id === resent.id ? resent : item)) ?? current,
      );
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteWebhookAction,
    onMutate: async ({ data }) => {
      await queryClient.cancelQueries({ queryKey: ["webhooks"] });
      const previous = queryClient.getQueryData<Awaited<ReturnType<typeof getWebhooksView>>>([
        "webhooks",
      ]);
      queryClient.setQueryData<Awaited<ReturnType<typeof getWebhooksView>>>(
        ["webhooks"],
        (current) => current?.filter((item) => item.id !== data.webhookId) ?? current,
      );
      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(["webhooks"], context.previous);
      }
    },
  });

  const rotateSecretMutation = useMutation({
    mutationFn: rotateWebhookSecretAction,
  });

  const testMutation = useMutation({
    mutationFn: testWebhookAction,
  });

  return (
    <WebhooksPage
      webhooks={webhooks}
      webhookAnimationStage={webhookAnimationStage}
      onCreate={async (input) => {
        return await createMutation.mutateAsync({ data: input });
      }}
      onUpdate={async (input) => {
        return await updateMutation.mutateAsync({ data: input });
      }}
      onDelete={async (webhookId) => {
        await deleteMutation.mutateAsync({ data: { webhookId } });
      }}
      onVerify={async (input) => {
        return await verifyMutation.mutateAsync({ data: input });
      }}
      onResendVerification={async (webhookId) => {
        return await resendMutation.mutateAsync({ data: { webhookId } });
      }}
      onRotateSecret={async (webhookId) => {
        return await rotateSecretMutation.mutateAsync({ data: { webhookId } });
      }}
      onTest={async (webhookId) => {
        await testMutation.mutateAsync({ data: { webhookId } });
      }}
    />
  );
}
